import { Truck, Building2, Monitor, Globe, Users, Zap, Gauge, BatteryCharging, MapPin, HeartHandshake, ShieldCheck, Wrench, BarChart3, ReceiptText, UserPlus, Route, Lock, Leaf, IndianRupee, Sparkles, Cpu, Activity, TrendingUp } from 'lucide-react';

export const NAV_LINKS = [
  { name: 'Home', path: '/' },
  { name: 'Mobility Verticals', path: '/mobility-verticals' },
  { name: 'Host', path: '/host' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

export const HERO_STATS = [
  { icon: Leaf, value: '11 Tonnes', label: 'CO₂ Avoided / Month' },
  { icon: Route, value: '2 Lakh+', label: 'Clean KMs / Month' },
  { icon: IndianRupee, value: '₹6 Lakh+', label: 'Rider Savings / Month' },
  { icon: HeartHandshake, value: '600+', label: 'Livelihoods Empowered / Month' },
];

export const VERTICALS = [
  {
    id: 'gearup',
    icon: Truck,
    title: 'GearUp!',
    tagline: 'Last-Mile Mobility',
    description: 'Electric two-wheelers built to keep delivery riders and businesses moving.',
    image: '/gearup_action_green.jpg',
    bullets: ['2-wheeler EV fleet', 'Flexible rental/subscription', 'Low cost per kilometer'],
    cta: 'Learn More',
    badge: null,
  },
  {
    id: 'campus',
    icon: Building2,
    title: 'Campus Go!',
    tagline: 'In-Campus Mobility',
    description: 'Connected electric mobility designed for seamless movement across large campuses.',
    image: '/campus_fleet.jpg',
    bullets: ['Electric carts / Electric cabs', 'Route-based operations', 'Ticket & pass system'],
    cta: 'Learn More',
    badge: null,
  },
  {
    id: 'orbit',
    icon: Globe,
    title: 'Orbit',
    tagline: 'Everyday Commute',
    description: 'A new electric mobility network built for everyday travel beyond the campus.',
    image: '/orbit_campus.jpg',
    bullets: ['Urban commute solution', 'Multi-modal connectivity', 'Subscription-based access'],
    cta: 'Learn More',
    badge: 'Coming Soon',
  },
];

export const HOW_IT_WORKS = [
  { step: 1, title: 'Get Fleet Requirement', desc: 'Tell us your business needs and vehicle requirements' },
  { step: 2, title: 'Deploy Vehicles + Onboarding', desc: 'We deliver vehicles and train your team' },
  { step: 3, title: 'Track & Scale via Software', desc: 'Monitor and optimize with our dashboard' },
];



export const DASHBOARD_FEATURES = [
  { icon: Cpu, title: 'Vehicle Intelligence', desc: 'IoT, GPS & connected vehicle data' },
  { icon: Activity, title: 'Mobility Operations', desc: 'Deployment, utilization & service workflows' },
  { icon: Users, title: 'Rider Experience', desc: 'Onboarding, navigation, subscriptions & support' },
  { icon: HeartHandshake, title: 'Asset Partner Network', desc: 'Host and leased asset management' },
  { icon: BarChart3, title: 'Business Intelligence', desc: 'Revenue, utilization & asset performance' },
];

export const GALLERY = [
  { title: 'GearUp', caption: 'Active delivery operations', image: '/action_delivery_1.jpg' },
  { title: 'Campus Go', caption: 'Managed campus shuttle', image: '/action_campus.jpg' },
  { title: 'Campus Transit', caption: 'Student shuttle commute in action', image: '/action_campus_passenger.jpg' },
  { title: 'Commute', caption: 'Reliable and accessible transit', image: '/action_commute_1.jpg' },
  { title: 'Operations', caption: 'Connected vehicles on the road', image: '/action_ops_1.jpg' },
  { title: 'Urban Mobility', caption: 'Powering micro-mobility everywhere', image: '/action_street_1.jpg' },
];

export const PARTNERS = ['Bounce', 'IIT Jammu', 'Battery Partners', 'Logistics Partners'];

export const TESTIMONIALS = [
  { quote: 'The EV is smooth, charging is fast, and I save so much on fuel. My earnings have increased!', name: 'Rahul Verma', role: 'Delivery Rider' },
  { quote: 'Campus Go transformed our campus transport. Students love it, and it’s eco-friendly!', name: 'Dr. Priya Mehta', role: 'Campus Admin, ABC College' },
  { quote: 'Switched to E Drives for my delivery fleet. Lower costs, better tracking, happy customers!', name: 'Amit Patel', role: 'Logistics Business Owner' },
];

export const SOLUTION_DETAILS = [
  {
    id: 'gearup',
    icon: Truck,
    title: 'GearUp! — Last-Mile Mobility',
    images: [
      '/gearup_action_green.jpg',
      '/gearup_action_black.jpg',
      '/gearup_fleet_green.jpg'
    ],
    tagline: "Subscription-led electric mobility built for India's delivery economy.",
    intro: "GearUp! enables delivery riders to access reliable electric two-wheelers through flexible subscription plans, backed by connected technology and on-ground operational support.",
    builtFor: ['Food Delivery', 'Quick Commerce', 'E-commerce', 'Urban Logistics'],
    whatWeEnable: [
      'Flexible EV subscriptions',
      'Connected vehicle technology',
      'Service & maintenance support',
      'Rider onboarding & assistance'
    ]
  },
  {
    id: 'campus',
    icon: Building2,
    title: 'Campus Go! — In-Campus Mobility',
    images: [
      '/campus_fleet.jpg',
      '/campus_cart_single.jpg'
    ],
    tagline: 'Managed electric mobility for campuses — without the upfront fleet investment.',
    intro: 'Campus Go! enables institutions to deploy dedicated electric mobility through long-term mobility contracts, combining vehicles, operations and technology in one managed service.',
    builtFor: ['Universities', 'Institutions', 'Large Campuses'],
    whatWeEnable: [
      'Long-term managed mobility contracts',
      'No upfront fleet investment',
      'Electric carts / Electric cabs',
      'Route-based mobility operations',
      'Drivers, service & maintenance support',
      'Technology-enabled operations'
    ]
  },
  {
    id: 'orbit',
    icon: Globe,
    title: 'Orbit — Everyday Commute',
    badge: 'Coming Soon',
    images: [
      '/orbit_commute.jpg',
      '/orbit_campus.jpg'
    ],
    tagline: 'Electric mobility designed for the journeys beyond the campus.',
    intro: 'Orbit is our upcoming mobility vertical connecting students and urban communities with structured, subscription-led electric commute options.',
    builtFor: ['Students', 'Young Professionals', 'Urban Communities'],
    featuresTitle: "What We're Building",
    whatWeEnable: [
      'Subscription-led mobility',
      'Structured commute networks',
      'Technology-enabled access',
      'Connected mobility experience'
    ],
    cta: 'Join Waitlist'
  },
];

export const TIMELINE = [
  {
    year: '2018',
    title: 'The Journey Begins',
    text: 'E Drives founded with a vision to make electric mobility more accessible.'
  },
  {
    year: '2021',
    title: 'GearUp! Takes Shape',
    text: "Built a subscription-led electric mobility model for India's delivery ecosystem."
  },
  {
    year: '2024',
    title: 'Building the Technology Layer',
    text: 'Connected vehicles, riders and mobility operations through our technology stack.'
  },
  {
    year: '2025',
    title: 'Campus Go! Goes Live',
    text: 'Expanded into managed in-campus mobility with our first institutional deployment.'
  },
  {
    year: '2026',
    title: 'Evolving Into a Mobility Platform',
    text: 'One connected EV platform powering GearUp!, Campus Go! and the upcoming Orbit vertical.'
  }
];

export const VALUES = [
  { icon: Zap, title: 'Built to Operate', desc: 'We build mobility services for real-world deployment and everyday operations.' },
  { icon: Cpu, title: 'Technology Connected', desc: 'Vehicles, riders, assets and operations connected through one technology layer.' },
  { icon: TrendingUp, title: 'Designed to Scale', desc: 'A multi-vertical platform built to expand across mobility needs and geographies.' },
];

export const BUSINESS_TYPES = [
  'GearUp! — Last-Mile Mobility',
  'Campus Go! — Campus Mobility',
  'Orbit — Everyday Commute',
  'Asset Partnership / Host',
  'Other'
];
